
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';
import { SmartConsultant } from '../SmartConsultant';
import { LeadFormModal } from '../LeadFormModal';
import { CustomProcess } from '../CustomProcess';
import { CustomCaseStudies } from '../CustomCaseStudies';
import {
  Calculator, Check, X, Loader2, MessageSquare, Database, Server, ShieldCheck, Lock, Minus, Plus, Cpu, ArrowRight, Activity, Terminal, Workflow, Network, GitBranch, Layers, Box, Settings, Bot, FileText, Mail, Zap, Users, FileSpreadsheet, Share2, Eye, Mic, Search, Clock, Stethoscope, Code2, Rocket, Grid, ChevronRight, Target
} from 'lucide-react';
import { Page } from '../../types';

interface CustomArchitecturePageProps {
  onNavigate?: (page: Page) => void;
}

interface CustomArchitecturePageProps {
  onNavigate?: (page: Page) => void;
}

// --- HERO ANIMATION: WORKFLOW ORCHESTRATION VISUAL ---

const NeuralOrchestratorVisual = () => {
  const { t } = useTranslation();
  const [activeNode, setActiveNode] = useState(0);
  const [packets, setPackets] = useState<{ id: number, angle: number, dist: number, status: 'in' | 'process' | 'out' }[]>([]);

  useEffect(() => {
    // Cycle active input nodes
    const interval = setInterval(() => {
      setActiveNode(prev => (prev + 1) % 4);
      // Spawn packet
      const angle = (activeNode * 90) + 45; // 4 corners
      setPackets(prev => [...prev, { id: Date.now(), angle, dist: 100, status: 'in' }]);
    }, 800);
    return () => clearInterval(interval);
  }, [activeNode]);

  useEffect(() => {
    // Animate packets
    const frame = setInterval(() => {
      setPackets(prev => prev.map(p => {
        if (p.status === 'in') {
          const newDist = p.dist - 5;
          return newDist <= 0 ? { ...p, dist: 0, status: 'process' as const } : { ...p, dist: newDist };
        }
        if (p.status === 'process') {
          // Hold in center for a bit then shoot out
          return Math.random() > 0.9 ? { ...p, status: 'out' as const } : p;
        }
        if (p.status === 'out') {
          return { ...p, dist: p.dist + 5 };
        }
        return p;
      }).filter(p => p.dist < 120)); // Remove when out of bounds
    }, 50);
    return () => clearInterval(frame);
  }, []);

  return (
    <div className="bg-[#0F172A] w-full h-full flex flex-col relative overflow-hidden p-6 md:p-10 border border-clever-teal/20 rounded-xl shadow-2xl min-h-[450px]">

      {/* Decorative Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(220,38,38,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(220,38,38,0.05)_1px,transparent_1px)] bg-[size:40px_40px]"></div>

      <div className="flex justify-between items-center mb-8 border-b border-clever-teal/20 pb-4 relative z-10">
        <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-clever-teal animate-pulse"></div><span className="text-xs font-mono text-red-300 uppercase tracking-widest">{t('shared.custom_solutions.visuals.neural.core')}</span></div>
        <div className="text-xs font-mono text-slate-500">/sys/master_control</div>
      </div>

      <div className="flex-1 relative flex items-center justify-center">

        {/* CENTRAL AI NODE (RED COMPUTER HEAD) */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
          <div className="w-24 h-24 bg-[#0B1121] border-2 border-clever-teal rounded-xl flex items-center justify-center shadow-[0_0_50px_rgba(217,38,38,0.6)] relative overflow-hidden">
            <div className="absolute inset-0 bg-clever-teal/10 animate-pulse"></div>
            <Bot size={48} className="text-clever-teal relative z-10" />
            {/* Scanning line inside */}
            <div className="absolute top-0 left-0 w-full h-1 bg-clever-teal/50 animate-[scan-down_2s_linear_infinite]"></div>
          </div>
        </div>

        {/* Satellites */}
        {[
          { label: "Mail", icon: <Mail size={16} />, x: -80, y: -80 },
          { label: "Excel", icon: <FileSpreadsheet size={16} />, x: 80, y: -80 },
          { label: "API", icon: <Network size={16} />, x: -80, y: 80 },
          { label: "DB", icon: <Database size={16} />, x: 80, y: 80 }
        ].map((node, i) => (
          <div
            key={i}
            className={`absolute w-12 h-12 bg-slate-800 border rounded-lg flex items-center justify-center z-10 transition-all duration-300 ${activeNode === i ? 'border-white text-white scale-110' : 'border-slate-600 text-slate-500'}`}
            style={{ transform: `translate(${node.x}px, ${node.y}px)` }}
          >
            {node.icon}
          </div>
        ))}

        {/* Data Packets */}
        {packets.map((p) => {
          const rad = p.angle * (Math.PI / 180);
          const x = Math.cos(rad) * p.dist;
          const y = Math.sin(rad) * p.dist;
          return (
            <div
              key={p.id}
              className={`absolute w-2 h-2 rounded-full shadow-sm z-10 transition-colors ${p.status === 'out' ? 'bg-emerald-400' : 'bg-white'}`}
              style={{ transform: `translate(${x}px, ${y}px)` }}
            ></div>
          )
        })}
      </div>

      <div className="mt-auto pt-6 flex justify-between text-[10px] font-mono text-slate-500 border-t border-clever-teal/20 relative z-10">
        <span>{t('shared.custom_solutions.visuals.neural.throughput')}</span>
        <span className="text-clever-teal">{t('shared.custom_solutions.visuals.neural.status')}</span>
      </div>

      <style>{`
        @keyframes scan-down {
            0% { top: 0; opacity: 0; }
            50% { opacity: 1; }
            100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </div>
  );
};

// --- HERO ANIMATION: WORKFLOW ORCHESTRATION VISUAL ---

const WorkflowCoreVisual = () => {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState<{ id: number, x: number, y: number, active: boolean, type: string }[]>([
    { id: 0, x: 50, y: 50, active: true, type: 'CORE' }, // Logic Core
    { id: 1, x: 20, y: 20, active: false, type: 'MAIL' },
    { id: 2, x: 80, y: 20, active: false, type: 'CRM' },
    { id: 3, x: 20, y: 80, active: false, type: 'DOCS' },
    { id: 4, x: 80, y: 80, active: false, type: 'CHAT' },
    { id: 5, x: 50, y: 20, active: false, type: 'API' },
  ]);

  useEffect(() => {
    // Randomly activate data flow to core
    const interval = setInterval(() => {
      setNodes(curr => curr.map(n => ({ ...n, active: n.id === 0 ? true : Math.random() > 0.6 })));
    }, 1200);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#0F172A] w-full h-full flex flex-col relative overflow-hidden p-6 md:p-10 border border-slate-700 rounded-xl shadow-2xl min-h-[400px]">

      {/* Header Stats */}
      <div className="flex justify-between items-center mb-8 border-b border-slate-700 pb-4 z-10 relative">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-clever-teal animate-pulse"></div>
          <span className="text-xs font-mono text-slate-300 uppercase tracking-widest">{t('shared.custom_solutions.visuals.workflow.engine')}</span>
        </div>
        <div className="text-xs font-mono text-slate-500">/automations/active</div>
      </div>

      {/* MAIN VISUAL AREA */}
      <div className="flex-1 relative">

        {/* Connecting Lines */}
        <svg className="absolute inset-0 w-full h-full">
          {nodes.slice(1).map((n) => (
            <line
              key={n.id}
              x1="50%" y1="50%"
              x2={`${n.x}%`} y2={`${n.y}%`}
              stroke={n.active ? "#ef4444" : "#334155"}
              strokeWidth={n.active ? "2" : "1"}
              strokeDasharray="4 4"
              className={n.active ? "animate-pulse" : ""}
            />
          ))}
        </svg>

        {/* Nodes */}
        {nodes.map(n => (
          <div
            key={n.id}
            className={`absolute w-12 h-12 rounded-lg border flex items-center justify-center transition-all duration-500 transform -translate-x-1/2 -translate-y-1/2 z-10
                  ${n.id === 0 ? 'bg-[#0B1121] border-clever-teal shadow-[0_0_30px_rgba(220,38,38,0.5)] scale-125' :
                n.active ? 'bg-slate-800 border-white text-white' : 'bg-slate-900 border-slate-800 text-slate-600'}
               `}
            style={{ left: `${n.x}%`, top: `${n.y}%` }}
          >
            {n.id === 0 ? <Bot size={24} className="text-clever-teal" /> :
              n.type === 'MAIL' ? <Mail size={16} /> :
                n.type === 'CRM' ? <Users size={16} /> :
                  n.type === 'DOCS' ? <FileText size={16} /> :
                    n.type === 'CHAT' ? <MessageSquare size={16} /> :
                      <Network size={16} />
            }
          </div>
        ))}

      </div>

      {/* Footer */}
      <div className="mt-auto pt-6 flex justify-between text-[10px] font-mono text-slate-500 border-t border-slate-700 relative z-10">
        <span>{t('shared.custom_solutions.visuals.workflow.tasks')}</span>
        <span className="text-clever-teal">{t('shared.custom_solutions.visuals.workflow.status')}</span>
      </div>
    </div>
  );
};

// --- ANIMATION: AUTOMATION STEPS VISUAL ---

const AutomationStepsVisual = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);

  // Simulation of a workflow
  const steps = [
    { id: 1, label: t('shared.custom_solutions.visuals.log.steps.s1'), status: "done" },
    { id: 2, label: t('shared.custom_solutions.visuals.log.steps.s2'), status: "processing" },
    { id: 3, label: t('shared.custom_solutions.visuals.log.steps.s3'), status: "pending" },
    { id: 4, label: t('shared.custom_solutions.visuals.log.steps.s4'), status: "pending" },
    { id: 5, label: t('shared.custom_solutions.visuals.log.steps.s5'), status: "pending" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setStep(prev => (prev + 1) % 6);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#0F172A] border border-slate-700 rounded-xl p-8 shadow-2xl relative overflow-hidden h-full min-h-[350px] flex flex-col justify-center">
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>

      <div className="relative z-10 w-full max-w-sm mx-auto flex flex-col gap-3">
        <div className="flex justify-between border-b border-slate-800 pb-2 mb-2 text-slate-500 font-mono text-xs">
          <span>{t('shared.custom_solutions.visuals.log.title')}</span>
          <span className="text-green-500 animate-pulse">{t('shared.custom_solutions.visuals.log.status')}</span>
        </div>

        {steps.map((s, i) => {
          let stateClass = "text-slate-600 border-slate-800";
          let icon = <Minus size={12} />;

          if (step > i) {
            stateClass = "text-green-400 border-green-900/30 bg-green-900/10";
            icon = <Check size={12} />;
          } else if (step === i) {
            stateClass = "text-white border-clever-teal/50 bg-slate-800";
            icon = <Loader2 size={12} className="animate-spin text-clever-teal" />;
          }

          return (
            <div key={s.id} className={`flex items-center gap-3 p-3 rounded border text-xs font-mono transition-all duration-300 ${stateClass}`}>
              <div className="shrink-0">{icon}</div>
              <span>{s.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- VERTICAL LOGIC CHAIN ANIMATION ---
const ProcessLogicChainVisual = () => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    { label: t('shared.custom_solutions.visuals.trace.steps.s1_label'), sub: t('shared.custom_solutions.visuals.trace.steps.s1_sub'), icon: <Mail size={14} /> },
    { label: t('shared.custom_solutions.visuals.trace.steps.s2_label'), sub: t('shared.custom_solutions.visuals.trace.steps.s2_sub'), icon: <Cpu size={14} /> },
    { label: t('shared.custom_solutions.visuals.trace.steps.s3_label'), sub: t('shared.custom_solutions.visuals.trace.steps.s3_sub'), icon: <Database size={14} /> },
    { label: t('shared.custom_solutions.visuals.trace.steps.s4_label'), sub: t('shared.custom_solutions.visuals.trace.steps.s4_sub'), icon: <FileText size={14} /> },
    { label: t('shared.custom_solutions.visuals.trace.steps.s5_label'), sub: t('shared.custom_solutions.visuals.trace.steps.s5_sub'), icon: <Check size={14} /> },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(s => (s + 1) % (steps.length + 1)); // +1 for pause at end
    }, 1200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#0F172A] border border-clever-teal/30 rounded-xl p-8 shadow-2xl relative overflow-hidden h-[600px] flex flex-col">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(#ef4444 1px, transparent 1px), linear-gradient(90deg, #ef4444 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>

      <div className="relative z-10 w-full max-w-xs mx-auto flex flex-col h-full justify-center">
        <div className="text-center mb-8">
          <span className="text-clever-teal font-mono text-xs uppercase tracking-widest border border-clever-teal/50 px-3 py-1 rounded-full">{t('shared.custom_solutions.visuals.trace.title')}</span>
        </div>

        <div className="space-y-6 relative">
          {/* Connecting Line */}
          <div className="absolute left-6 top-4 bottom-4 w-0.5 bg-slate-800 -z-10">
            <div
              className="w-full bg-clever-teal transition-all duration-300"
              style={{ height: `${Math.min(100, (activeStep / (steps.length - 1)) * 100)}%` }}
            ></div>
          </div>

          {steps.map((step, i) => {
            const isActive = activeStep === i;
            const isDone = activeStep > i;

            return (
              <div key={i} className={`flex items-center gap-4 transition-all duration-500 ${isActive ? 'scale-105' : 'opacity-60'}`}>
                <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center shrink-0 bg-[#0B1121] z-20 transition-colors ${isActive ? 'border-clever-teal text-white shadow-[0_0_20px_rgba(220,38,38,0.6)]' :
                  isDone ? 'border-emerald-500 text-emerald-500' :
                    'border-slate-700 text-slate-600'
                  }`}>
                  {step.icon}
                </div>
                <div className={`p-4 rounded-lg border w-full transition-all ${isActive ? 'bg-slate-800 border-clever-teal text-white' :
                  'bg-slate-900 border-slate-800 text-slate-500'
                  }`}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">{step.sub}</span>
                    {isActive && <span className="w-2 h-2 bg-clever-teal rounded-full animate-ping"></span>}
                  </div>
                  <div className="text-xs font-bold">{step.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// --- TABS CONTENT ---

const CustomExpertiseTabs = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    {
      label: t('shared.custom_solutions.tabs.items.workflow.label'),
      icon: <Workflow size={16} />,
      title: t('shared.custom_solutions.tabs.items.workflow.title'),
      content: [
        t('shared.custom_solutions.tabs.items.workflow.content_0'),
        t('shared.custom_solutions.tabs.items.workflow.content_1'),
        t('shared.custom_solutions.tabs.items.workflow.content_2')
      ],
      specs: [
        { label: t('shared.custom_solutions.tabs.items.workflow.specs.tools.label'), val: t('shared.custom_solutions.tabs.items.workflow.specs.tools.val') },
        { label: t('shared.custom_solutions.tabs.items.workflow.specs.goal.label'), val: t('shared.custom_solutions.tabs.items.workflow.specs.goal.val') },
        { label: t('shared.custom_solutions.tabs.items.workflow.specs.effect.label'), val: t('shared.custom_solutions.tabs.items.workflow.specs.effect.val') }
      ]
    },
    {
      label: t('shared.custom_solutions.tabs.items.rag.label'),
      icon: <Bot size={16} />,
      title: t('shared.custom_solutions.tabs.items.rag.title'),
      content: [
        t('shared.custom_solutions.tabs.items.rag.content_0'),
        t('shared.custom_solutions.tabs.items.rag.content_1'),
        t('shared.custom_solutions.tabs.items.rag.content_2')
      ],
      specs: [
        { label: t('shared.custom_solutions.tabs.items.rag.specs.tech.label'), val: t('shared.custom_solutions.tabs.items.rag.specs.tech.val') },
        { label: t('shared.custom_solutions.tabs.items.rag.specs.knowledge.label'), val: t('shared.custom_solutions.tabs.items.rag.specs.knowledge.val') },
        { label: t('shared.custom_solutions.tabs.items.rag.specs.privacy.label'), val: t('shared.custom_solutions.tabs.items.rag.specs.privacy.val') }
      ]
    },
    {
      label: t('shared.custom_solutions.tabs.items.tools.label'),
      icon: <Settings size={16} />,
      title: t('shared.custom_solutions.tabs.items.tools.title'),
      content: [
        t('shared.custom_solutions.tabs.items.tools.content_0'),
        t('shared.custom_solutions.tabs.items.tools.content_1'),
        t('shared.custom_solutions.tabs.items.tools.content_2')
      ],
      specs: [
        { label: t('shared.custom_solutions.tabs.items.tools.specs.type.label'), val: t('shared.custom_solutions.tabs.items.tools.specs.type.val') },
        { label: t('shared.custom_solutions.tabs.items.tools.specs.ui.label'), val: t('shared.custom_solutions.tabs.items.tools.specs.ui.val') },
        { label: t('shared.custom_solutions.tabs.items.tools.specs.fit.label'), val: t('shared.custom_solutions.tabs.items.tools.specs.fit.val') }
      ]
    },
    {
      label: t('shared.custom_solutions.tabs.items.integration.label'),
      icon: <Network size={16} />,
      title: t('shared.custom_solutions.tabs.items.integration.title'),
      content: [
        t('shared.custom_solutions.tabs.items.integration.content_0'),
        t('shared.custom_solutions.tabs.items.integration.content_1'),
        t('shared.custom_solutions.tabs.items.integration.content_2')
      ],
      specs: [
        { label: t('shared.custom_solutions.tabs.items.integration.specs.connect.label'), val: t('shared.custom_solutions.tabs.items.integration.specs.connect.val') },
        { label: t('shared.custom_solutions.tabs.items.integration.specs.sync.label'), val: t('shared.custom_solutions.tabs.items.integration.specs.sync.val') },
        { label: t('shared.custom_solutions.tabs.items.integration.specs.error.label'), val: t('shared.custom_solutions.tabs.items.integration.specs.error.val') }
      ]
    }
  ];

  return (
    <div className="bg-white border border-gray-200 shadow-sm">
      <div className="flex overflow-x-auto border-b border-gray-200 bg-gray-50/50 no-scrollbar">
        {tabs.map((tab, idx) => (
          <button
            key={idx}
            onClick={() => setActiveTab(idx)}
            className={`
              flex items-center gap-3 px-6 md:px-8 py-6 text-xs font-bold uppercase tracking-widest transition-all duration-300 border-r border-gray-200 whitespace-nowrap flex-shrink-0
              ${activeTab === idx
                ? 'bg-white text-clever-teal border-b-2 border-b-clever-teal shadow-none'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100 border-b border-transparent'}
            `}
          >
            {tab.icon}
            <span className="inline">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[500px]">
        <div className="lg:col-span-8 p-12 lg:p-16 bg-white">
          <div className="inline-block text-clever-teal text-[10px] font-mono font-bold uppercase tracking-widest mb-6 border border-red-100 px-3 py-1 bg-red-50">
            // Architecture Protocol: 0{activeTab + 1}
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10 leading-tight">
            {tabs[activeTab].title}
          </h3>
          <div className="space-y-6 text-gray-600 text-lg font-light leading-relaxed">
            {tabs[activeTab].content.map((paragraph, pIdx) => (
              <p key={pIdx}>{paragraph}</p>
            ))}
          </div>
        </div>

        <div className="lg:col-span-4 bg-[#0B1121] text-white p-12 lg:p-16 flex flex-col justify-center relative overflow-hidden border-t lg:border-t-0 lg:border-l border-gray-800">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(#ef4444 1px, transparent 1px), linear-gradient(90deg, #ef4444 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-8 text-clever-teal">
              <Cpu size={20} />
              <span className="font-bold uppercase tracking-widest text-xs">{t('shared.custom_solutions.tabs.specs_label')}</span>
            </div>
            <div className="space-y-8">
              {tabs[activeTab].specs.map((spec, sIdx) => (
                <div key={sIdx} className="border-l-2 border-red-500/30 pl-6 animate-fade-in" style={{ animationDelay: `${sIdx * 100}ms` }}>
                  <div className="text-[10px] text-gray-500 uppercase tracking-[0.2em] mb-2 font-mono">
                    {spec.label}
                  </div>
                  <div className="text-xl font-mono font-medium text-white">
                    {spec.val}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-12 pt-8 border-t border-red-500/20">
              <div className="flex items-center gap-3 text-xs text-gray-400 font-mono">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                {t('shared.custom_solutions.tabs.module_active')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomFAQ = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      id: "CUST_01",
      q: t('shared.custom_solutions.faq.items.01.q'),
      a: t('shared.custom_solutions.faq.items.01.a')
    },
    {
      id: "CUST_02",
      q: t('shared.custom_solutions.faq.items.02.q'),
      a: t('shared.custom_solutions.faq.items.02.a')
    },
    {
      id: "CUST_03",
      q: t('shared.custom_solutions.faq.items.03.q'),
      a: t('shared.custom_solutions.faq.items.03.a')
    },
    {
      id: "CUST_04",
      q: t('shared.custom_solutions.faq.items.04.q'),
      a: t('shared.custom_solutions.faq.items.04.a')
    },
    {
      id: "CUST_05",
      q: t('shared.custom_solutions.faq.items.05.q'),
      a: t('shared.custom_solutions.faq.items.05.a')
    }
  ];

  return (
    <div className="flex flex-col border-t border-gray-200">
      {faqs.map((faq, index) => (
        <Reveal key={index} delay={index * 0.1}>
          <div className={`group border-b border-gray-200 transition-colors duration-300 ${openIndex === index ? 'bg-red-50/30' : 'hover:bg-red-50/10'}`}>
            <button
              className="w-full flex items-start md:items-center justify-between py-10 px-4 md:px-8 text-left"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-12">
                <span className="font-mono text-xs text-gray-400 uppercase tracking-widest">
                  // {faq.id}
                </span>
                <span className={`text-xl md:text-2xl font-bold transition-colors duration-300 ${openIndex === index ? 'text-clever-teal' : 'text-gray-900'}`}>
                  {faq.q}
                </span>
              </div>

              <div className="ml-4 flex-shrink-0 text-gray-400 font-mono">
                {openIndex === index ? <Minus size={20} /> : <Plus size={20} />}
              </div>
            </button>

            <div
              className={`overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] ${openIndex === index ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}
            >
              <div className="px-4 md:px-8 pb-10 grid grid-cols-1 md:grid-cols-12">
                <div className="hidden md:block md:col-span-2"></div>
                <div className="md:col-span-8">
                  <div className="text-lg leading-relaxed text-gray-600 font-light pl-4 border-l-2 border-red-500/30">
                    {faq.a}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
};

// --- MAIN PAGE COMPONENT ---

export const CustomSolutionsPage: React.FC<CustomArchitecturePageProps> = ({ onNavigate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  const problems = [
    { title: t('shared.custom_solutions.pain_points.items.media_break.title'), desc: t('shared.custom_solutions.pain_points.items.media_break.desc'), icon: <FileSpreadsheet className="text-red-400" />, highlight: t('shared.custom_solutions.pain_points.items.media_break.highlight') },
    { title: t('shared.custom_solutions.pain_points.items.search.title'), desc: t('shared.custom_solutions.pain_points.items.search.desc'), icon: <Search className="text-orange-400" />, highlight: t('shared.custom_solutions.pain_points.items.search.highlight') },
    { title: t('shared.custom_solutions.pain_points.items.routine.title'), desc: t('shared.custom_solutions.pain_points.items.routine.desc'), icon: <Clock className="text-yellow-400" />, highlight: t('shared.custom_solutions.pain_points.items.routine.highlight') },
    { title: t('shared.custom_solutions.pain_points.items.tools.title'), desc: t('shared.custom_solutions.pain_points.items.tools.desc'), icon: <Zap className="text-blue-400" />, highlight: t('shared.custom_solutions.pain_points.items.tools.highlight') }
  ];

  // Defined here to be available for the render loop below
  const steps = [
    {
      id: "01",
      title: t('shared.custom_solutions.steps.items.01.title'),
      subtitle: t('shared.custom_solutions.steps.items.01.subtitle'),
      desc: t('shared.custom_solutions.steps.items.01.desc'),
      icon: <Stethoscope size={24} />,
      points: t('shared.custom_solutions.steps.items.01.points', { returnObjects: true }) as string[]
    },
    {
      id: "02",
      title: t('shared.custom_solutions.steps.items.02.title'),
      subtitle: t('shared.custom_solutions.steps.items.02.subtitle'),
      desc: t('shared.custom_solutions.steps.items.02.desc'),
      icon: <Cpu size={24} />,
      points: t('shared.custom_solutions.steps.items.02.points', { returnObjects: true }) as string[]
    },
    {
      id: "03",
      title: t('shared.custom_solutions.steps.items.03.title'),
      subtitle: t('shared.custom_solutions.steps.items.03.subtitle'),
      desc: t('shared.custom_solutions.steps.items.03.desc'),
      icon: <Code2 size={24} />,
      points: t('shared.custom_solutions.steps.items.03.points', { returnObjects: true }) as string[]
    },
    {
      id: "04",
      title: t('shared.custom_solutions.steps.items.04.title'),
      subtitle: t('shared.custom_solutions.steps.items.04.subtitle'),
      desc: t('shared.custom_solutions.steps.items.04.desc'),
      icon: <Rocket size={24} />,
      points: t('shared.custom_solutions.steps.items.04.points', { returnObjects: true }) as string[]
    }
  ];

  return (
    <div className="bg-white min-h-screen pt-24 font-sans text-gray-900 selection:bg-clever-teal selection:text-white">

      {/* 1. HERO SECTION */}
      <section className="relative pt-20 pb-32 px-6 overflow-hidden bg-white border-b border-gray-100">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-red-50 rounded-full blur-[120px] pointer-events-none translate-x-1/3 -translate-y-1/3"></div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16 items-center relative z-10">

          {/* Text Side */}
          <div className="lg:col-span-6">
            <Reveal>
              <div className="inline-block px-3 py-1 mb-6 rounded-full border border-clever-teal/30 bg-clever-teal/10 text-clever-teal text-xs font-mono uppercase tracking-widest">
                {t('shared.custom_solutions.hero.badge')}
              </div>
              <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
                {t('shared.custom_solutions.hero.title_line1')}<br />
                {t('shared.custom_solutions.hero.title_line2')}
              </h1>
              <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto leading-relaxed">
                {t('shared.custom_solutions.hero.subtitle')}
              </p>

              <div className="flex flex-wrap gap-4 mb-12">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-clever-teal text-white px-8 py-4 font-bold uppercase tracking-widest text-xs hover:bg-red-800 transition-all shadow-xl hover:shadow-red-600/20 rounded-sm flex items-center gap-2"
                >
                  {t('shared.custom_solutions.hero.cta_analyze')} <ArrowRight size={16} />
                </button>
                <button
                  onClick={() => onNavigate && onNavigate('roi')}
                  className="bg-white text-gray-900 border border-gray-200 px-8 py-4 font-bold uppercase tracking-widest text-xs hover:border-clever-teal hover:text-clever-teal transition-all rounded-sm flex items-center gap-2"
                >
                  <Calculator size={16} />
                  {t('shared.custom_solutions.hero.cta_roi')}
                </button>
              </div>
            </Reveal>
          </div>

          {/* Visual Side */}
          <div className="lg:col-span-6 relative flex items-center justify-center py-12">
            <Reveal delay={0.2}>
              <div className="w-full min-h-[450px] md:min-h-[500px]">
                <NeuralOrchestratorVisual />
              </div>
            </Reveal>
          </div>

        </div>
      </section>

      {/* 2. REAL WORLD PRODUCTS (NEW SECTION) */}
      <section className="py-32 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-300 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                {t('shared.custom_solutions.products.title_line1')}<br />{t('shared.custom_solutions.products.title_line2')}
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-500 text-lg max-w-md">
                  {t('shared.custom_solutions.products.subtitle')}
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Product 1 */}
            <Reveal delay={0.1}>
              <div className="bg-white p-10 rounded-xl shadow-lg border border-gray-100 hover:border-clever-teal/30 transition-colors h-full flex flex-col">
                <div className="w-16 h-16 bg-red-50 text-clever-teal flex items-center justify-center rounded-2xl mb-8">
                  <FileText size={32} />
                </div>
                <div className="flex flex-col h-full">
                  <h3 className="text-2xl font-bold mb-4">{t('shared.custom_solutions.products.items.invoice.title')}</h3>
                  <div className="text-sm font-mono text-gray-500 mb-4 bg-gray-50 p-4 rounded border border-gray-200">
                    <span className="text-red-600 font-bold">{t('shared.custom_solutions.products.items.invoice.problem_label')}</span> {t('shared.custom_solutions.products.items.invoice.problem')}
                  </div>
                  <div className="text-sm text-gray-700 mb-6">
                    <strong className="text-green-600">{t('shared.custom_solutions.products.items.invoice.solution_label')}</strong> {t('shared.custom_solutions.products.items.invoice.solution')}
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-200 pt-4 mt-auto">
                    <span className="text-clever-teal font-mono font-bold">{t('shared.custom_solutions.products.items.invoice.roi')}</span>
                    <button className="text-gray-600 hover:text-clever-teal flex items-center gap-1 text-sm font-bold transition-colors" onClick={() => setIsModalOpen(true)}>{t('shared.custom_solutions.products.items.invoice.cta')} <ChevronRight size={16} /></button>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Product 2 */}
            <Reveal delay={0.2}>
              <div className="bg-white p-10 rounded-xl shadow-lg border border-gray-100 hover:border-clever-teal/30 transition-colors h-full flex flex-col">
                <div className="w-16 h-16 bg-red-50 text-clever-teal flex items-center justify-center rounded-2xl mb-8">
                  <Target size={32} />
                </div>
                <div className="flex flex-col h-full">
                  <h3 className="text-2xl font-bold mb-4">{t('shared.custom_solutions.products.items.sales.title')}</h3>
                  <div className="text-sm font-mono text-gray-500 mb-4 bg-gray-50 p-4 rounded border border-gray-200">
                    <span className="text-red-600 font-bold">{t('shared.custom_solutions.products.items.sales.problem_label')}</span> {t('shared.custom_solutions.products.items.sales.problem')}
                  </div>
                  <div className="text-sm text-gray-700 mb-6">
                    <strong className="text-green-600">{t('shared.custom_solutions.products.items.sales.solution_label')}</strong> {t('shared.custom_solutions.products.items.sales.solution')}
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-200 pt-4 mt-auto">
                    <span className="text-clever-teal font-mono font-bold">{t('shared.custom_solutions.products.items.sales.roi')}</span>
                    <button className="text-gray-600 hover:text-clever-teal flex items-center gap-1 text-sm font-bold transition-colors" onClick={() => setIsModalOpen(true)}>{t('shared.custom_solutions.products.items.sales.cta')} <ChevronRight size={16} /></button>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section >

      {/* 3. TECH STACK EXPANDED (9 Items Grid) */}
      < section className="py-32 bg-[#0B1121] text-white border-y border-gray-800" >
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-800 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-white tracking-tighter leading-none">
                {t('shared.custom_solutions.tech_stack.title_line1')}<br />{t('shared.custom_solutions.tech_stack.title_line2')}
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-400 text-lg max-w-md">
                  {t('shared.custom_solutions.tech_stack.subtitle')}
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { id: "agentic", icon: <Bot />, title: t('shared.custom_solutions.tech_stack.items.agentic.title'), desc: t('shared.custom_solutions.tech_stack.items.agentic.desc') },
              { id: "rag", icon: <Database />, title: t('shared.custom_solutions.tech_stack.items.rag.title'), desc: t('shared.custom_solutions.tech_stack.items.rag.desc') },
              { id: "tool", icon: <Workflow />, title: t('shared.custom_solutions.tech_stack.items.tool_chaining.title'), desc: t('shared.custom_solutions.tech_stack.items.tool_chaining.desc') },
              { id: "api", icon: <Server />, title: t('shared.custom_solutions.tech_stack.items.api.title'), desc: t('shared.custom_solutions.tech_stack.items.api.desc') },
              { id: "vision", icon: <Eye />, title: t('shared.custom_solutions.tech_stack.items.vision.title'), desc: t('shared.custom_solutions.tech_stack.items.vision.desc') },
              { id: "voice", icon: <Mic />, title: t('shared.custom_solutions.tech_stack.items.voice.title'), desc: t('shared.custom_solutions.tech_stack.items.voice.desc') },
              { id: "mining", icon: <Activity />, title: t('shared.custom_solutions.tech_stack.items.process_mining.title'), desc: t('shared.custom_solutions.tech_stack.items.process_mining.desc') },
              { id: "warehouse", icon: <Grid />, title: t('shared.custom_solutions.tech_stack.items.warehouse.title'), desc: t('shared.custom_solutions.tech_stack.items.warehouse.desc') },
              { id: "security", icon: <Lock />, title: t('shared.custom_solutions.tech_stack.items.security.title'), desc: t('shared.custom_solutions.tech_stack.items.security.desc') }
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="p-8 border border-gray-800 bg-gray-900/50 hover:bg-gray-800 transition-colors rounded-lg h-full flex flex-col group">
                  <div className="mb-4 p-3 bg-white/5 w-fit rounded-lg border border-white/10 group-hover:border-clever-teal/50 transition-colors">{item.icon}</div>
                  <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section >

      {/* 4. PRICING MODELS */}
      < section className="py-32 bg-white" >
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-200 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                {t('shared.custom_solutions.pricing.title_line1')}<br />{t('shared.custom_solutions.pricing.title_line2')}
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-500 text-lg max-w-md">
                  {t('shared.custom_solutions.pricing.subtitle')}
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">

            {/* PROJECT MODEL */}
            <div className="border border-gray-200 p-10 rounded-xl relative overflow-hidden group hover:border-clever-teal transition-colors bg-white shadow-sm">
              <div className="absolute top-0 left-0 bg-gray-100 px-4 py-1 text-xs font-bold uppercase tracking-widest text-gray-600 rounded-br-lg">
                {t('shared.custom_solutions.pricing.models.project.badge')}
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2 mt-4">{t('shared.custom_solutions.pricing.models.project.title')}</h3>
              <p className="text-gray-500 mb-8">{t('shared.custom_solutions.pricing.models.project.desc')}</p>

              <div className="space-y-4 mb-8">
                <div className="flex gap-3 items-center">
                  <Check className="text-clever-teal" size={18} />
                  <span className="text-gray-700"><strong>{t('shared.custom_solutions.pricing.models.project.features.ip_label')}:</strong> {t('shared.custom_solutions.pricing.models.project.features.ip_val')}</span>
                </div>
                <div className="flex gap-3 items-center">
                  <Check className="text-clever-teal" size={18} />
                  <span className="text-gray-700">{t('shared.custom_solutions.pricing.models.project.features.cost')}</span>
                </div>
                <div className="flex gap-3 items-center">
                  <Check className="text-clever-teal" size={18} />
                  <span className="text-gray-700">{t('shared.custom_solutions.pricing.models.project.features.license')}</span>
                </div>
              </div>
              <div className="text-sm text-gray-400 font-mono pt-6 border-t border-gray-100">
                {t('shared.custom_solutions.pricing.models.project.ideal_for')}
              </div>
            </div>

            {/* LICENSE MODEL */}
            <div className="border border-clever-teal bg-red-50/20 p-10 rounded-xl relative overflow-hidden shadow-md">
              <div className="absolute top-0 left-0 bg-clever-teal px-4 py-1 text-xs font-bold uppercase tracking-widest text-white rounded-br-lg">
                {t('shared.custom_solutions.pricing.models.license.badge')}
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2 mt-4">{t('shared.custom_solutions.pricing.models.license.title')}</h3>
              <p className="text-gray-500 mb-8">{t('shared.custom_solutions.pricing.models.license.desc')}</p>

              <div className="space-y-4 mb-8">
                <div className="flex gap-3 items-center">
                  <Check className="text-clever-teal" size={18} />
                  <span className="text-gray-700"><strong>{t('shared.custom_solutions.pricing.models.license.features.service_label')}:</strong> {t('shared.custom_solutions.pricing.models.license.features.service_val')}</span>
                </div>
                <div className="flex gap-3 items-center">
                  <Check className="text-clever-teal" size={18} />
                  <span className="text-gray-700">{t('shared.custom_solutions.pricing.models.license.features.fee')}</span>
                </div>
                <div className="flex gap-3 items-center">
                  <Check className="text-clever-teal" size={18} />
                  <span className="text-gray-700">{t('shared.custom_solutions.pricing.models.license.features.capex')}</span>
                </div>
              </div>
              <div className="text-sm text-gray-400 font-mono pt-6 border-t border-red-100">
                {t('shared.custom_solutions.pricing.models.license.ideal_for')}
              </div>
            </div>

          </div>
        </div>
      </section >

      {/* 5. PROCESS & ANIMATION */}
      < section className="py-32 bg-gray-50 relative overflow-hidden" >
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-32 border-b border-gray-200 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                {t('shared.custom_solutions.steps_header.title_line1')}<br />{t('shared.custom_solutions.steps_header.title_line2')}
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-500 text-lg max-w-md">
                  {t('shared.custom_solutions.steps_header.subtitle')}
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-24 items-center">
            <div>
              <div className="space-y-16">
                {steps.map((step, index) => (
                  <Reveal key={step.id} delay={index * 0.1}>
                    <div className="pl-8 border-l-2 border-gray-200 relative group hover:border-clever-teal transition-colors duration-500">
                      <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-gray-300 border-4 border-white shadow-sm group-hover:bg-clever-teal transition-colors duration-500"></div>
                      <span className="text-xs font-bold uppercase tracking-widest text-clever-teal mb-2 block">{step.subtitle}</span>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-gray-500 leading-relaxed mb-4">{step.desc}</p>
                      <div className="flex flex-wrap gap-2">
                        {step.points.map((pt, idx) => (
                          <span key={idx} className="px-2 py-1 bg-white text-gray-600 text-[10px] font-bold uppercase tracking-wider border border-gray-200 rounded-sm">
                            {pt}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <div className="h-[600px] sticky top-32 lg:block hidden">
              <Reveal delay={0.2} className="h-full">
                <ProcessLogicChainVisual />
              </Reveal>
            </div>
          </div>
        </div>
      </section >

      {/* 6. CHAT CONSULTANT */}
      < section className="py-40 bg-[#0B1121] text-white overflow-hidden" >
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-20">
              <div className="inline-block px-4 py-2 bg-red-900/30 text-red-300 text-xs font-bold uppercase tracking-[0.2em] mb-6 border border-red-500/30">
                CleverDigital Consultant
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                {t('shared.custom_solutions.consultant.title')}
              </h2>
              <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                {t('shared.custom_solutions.consultant.desc')}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <SmartConsultant
              title={t('shared.custom_solutions.consultant.bot_title')}
              subtitle={t('shared.custom_solutions.consultant.tagline')}
              initialMessage={t('shared.custom_solutions.consultant.initial_message')}
              context="custom_solutions"
            />
          </Reveal>
        </div>
      </section >

      {/* 7. FAQ */}
      < section className="py-32 bg-white border-t border-gray-200" >
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                TECH<br />FAQ
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-500 text-lg max-w-md">
                  {t('shared.custom_solutions.faq.subtitle')}
                </p>
              </div>
            </div>
          </Reveal>
          <CustomFAQ />
        </div>
      </section >

      {/* CONTACT MODAL */}
      < CustomCaseStudies />
      <LeadFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} sector="Custom Solutions" />
    </div >
  );
};

